import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Step = ({ num, title, desc }) => (
  <div className="flex gap-4">
    <div className="w-8 h-8 bg-sky-500/20 border border-sky-500/30 rounded-xl flex items-center justify-center shrink-0 font-black text-sky-400 text-sm">{num}</div>
    <div className="min-w-0 pt-0.5">
      <h4 className="text-sm font-black text-white mb-1">{title}</h4>
      <p className="text-gray-400 text-xs leading-relaxed">{desc}</p>
    </div>
  </div>
);

const DocSection = ({ title, badge, children }) => (
  <div className="bg-white/[0.02] border border-white/[0.07] rounded-3xl p-8 mb-6">
    {badge && <span className="inline-block bg-sky-500/10 border border-sky-500/20 text-sky-400 text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest mb-4">{badge}</span>}
    <h2 className="text-xl font-black text-white uppercase tracking-tight mb-6">{title}</h2>
    {children}
  </div>
);

const DocsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('participants');

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
          </div>
          <button onClick={() => navigate(-1)} className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest transition-colors">← Back</button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-6 pt-32 pb-24">
        <div className="mb-12">
          <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-2">Documentation</p>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3">How EventDeck Works</h1>
          <p className="text-gray-400 text-sm max-w-xl">Complete guide to using EventDeck — whether you're registering for events or organising them.</p>
        </div>

        <div className="flex gap-2 mb-8">
          {[
            { key: 'participants', label: 'Participants' },
            { key: 'organisers', label: 'Organisers' },
            { key: 'features', label: 'Features' },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === t.key ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5 hover:text-white'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'participants' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <DocSection title="Registering for an Event" badge="Getting Started">
              <div className="space-y-5">
                <Step num="1" title="Browse Events" desc="Visit the EventDeck homepage. Use the search bar to find events by name, or filter by category using the pills. Click on any event card to view full details." />
                <Step num="2" title="Select a Segment" desc="Most events have multiple segments or categories. Review the available segments, their fees, and perks. Click 'Register' on your chosen segment." />
                <Step num="3" title="Fill Registration Form" desc="Enter your full name, email, and phone number. Complete any additional fields required by the organiser (e.g., institution, occupation). Apply a promo code if you have one." />
                <Step num="4" title="Make Payment" desc="Send the exact fee amount to the displayed mobile banking number (bKash, Nagad, or Rocket). Copy the number using the Copy button to avoid errors." />
                <Step num="5" title="Submit Transaction ID" desc="After sending payment, enter your Transaction ID (TrxID) from your mobile banking app, your sender number, and select the payment method. Click Complete Registration." />
                <Step num="6" title="Wait for Verification" desc="The organiser will verify your payment (typically within 24-48 hours). You'll receive a confirmation email with your digital QR ticket once verified." />
              </div>
            </DocSection>

            <DocSection title="Your Tickets & Account" badge="Account">
              <div className="space-y-5">
                <Step num="1" title="Create an Account" desc="Click 'Sign In' in the navbar and select 'Create Account'. Fill in your details. Having an account lets you track all your registrations in one place." />
                <Step num="2" title="My Tickets" desc="Go to My Tickets from the navbar to see all your registrations. Filter by Verified, Pending, or All. Download or view your QR codes anytime." />
                <Step num="3" title="QR Code" desc="Once your ticket is verified, click 'Show QR Code' on any ticket card. Download it to your device for offline access at the event gate." />
              </div>
            </DocSection>

            <DocSection title="At the Event" badge="Day Of">
              <div className="space-y-5">
                <Step num="1" title="Prepare Your Ticket" desc="Have your QR code ready — either downloaded on your device or accessible in My Tickets. Make sure your screen brightness is turned up for easy scanning." />
                <Step num="2" title="Gate Check-In" desc="Present your QR code to the gate staff. They'll scan it using EventDeck's scanner. Once scanned successfully, you'll be marked as checked in." />
                <Step num="3" title="One-Time Use" desc="Each QR code can only be scanned once. If you've already been checked in and try to scan again, the system will show 'Duplicate Entry'." />
              </div>
            </DocSection>
          </div>
        )}

        {activeTab === 'organisers' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <DocSection title="Setting Up Your Event" badge="Getting Started">
              <div className="space-y-5">
                <Step num="1" title="Get Portal Access" desc="Contact us at organisers@eventdeck.app to set up your organiser account. We'll configure your portal within 1-2 business days." />
                <Step num="2" title="Configure Payment" desc="In Payment Infrastructure, enter your mobile banking numbers (bKash, Nagad, Rocket). These will be shown to participants on the registration form." />
                <Step num="3" title="Create Segments" desc="In Segment Control, add segments for your event. Set titles, fees, descriptions, access notes, and registration deadlines. Add custom form fields as needed." />
                <Step num="4" title="Add Perks" desc="For each segment, add 'What's Included' perks (e.g., Lunch Included, Certificate, T-Shirt). These appear as bullet points on the segment card." />
                <Step num="5" title="Set Event Details" desc="In Event Settings, add your event name, banner image, venue, dates, and times. Upload your organiser logo and bio for the 'Organised By' section." />
              </div>
            </DocSection>

            <DocSection title="Managing Registrations" badge="Operations">
              <div className="space-y-5">
                <Step num="1" title="Verify Payments" desc="Go to Verify Payments to see all pending registrations. Review each submission's transaction ID against your mobile banking records. Click 'Verify & Ticket' to approve and send the QR ticket automatically." />
                <Step num="2" title="Reject Invalid Payments" desc="If a transaction ID is invalid or payment not received, click 'Failure Mail' to notify the participant and remove the registration." />
                <Step num="3" title="Export Data" desc="In the Intelligence tab, view all registration data and analytics. Click 'CSV' to download. Use the Columns picker to customise which data to export." />
                <Step num="4" title="Gate Management" desc="Set your gate PIN in Event Settings. Staff can access the scanner by clicking 'Staff' on the homepage, entering the PIN, and scanning QR codes." />
              </div>
            </DocSection>

            <DocSection title="Advanced Features" badge="Pro">
              <div className="space-y-5">
                <Step num="1" title="Promo Codes" desc="In Promo Codes, create discount codes with fixed BDT or percentage discounts. Optionally restrict codes to specific segments." />
                <Step num="2" title="Slot Management" desc="Add time slots to segments with daily, rolling, or static inventory modes. Set capacity per slot and exclude specific days." />
                <Step num="3" title="Merchandise" desc="Add merchandise items in the Merchandise tab. Participants can order from the Merch tab on your event page. Manage and fulfill orders from the portal." />
                <Step num="4" title="Theater Mode" desc="Add sponsors with logos and ad banners. Access Theater Mode at /event/{id}/theater for live event display screens." />
                <Step num="5" title="Ad Management" desc="If enabled, upload sponsor ad banners for your event page. Ad Slot 1 appears below the hero, Ad Slot 2 appears in the sidebar." />
              </div>
            </DocSection>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            {[
              { icon: '🎫', title: 'Digital QR Tickets', desc: 'Auto-generated QR code tickets sent by email upon payment verification. Downloadable from My Tickets anytime.' },
              { icon: '📱', title: 'Mobile-First Design', desc: 'Fully responsive — works seamlessly on all devices. Participants can register and access tickets from any device.' },
              { icon: '🛒', title: 'Registration Cart', desc: 'Participants can add multiple segments to cart and check out together with a single payment.' },
              { icon: '🏷️', title: 'Promo Codes', desc: 'Create discount codes with fixed or percentage discounts. Restrict to specific segments.' },
              { icon: '⏱️', title: 'Slot Management', desc: 'Time-slot based registration with daily, rolling, or static inventory. Automatic slot expiry.' },
              { icon: '📊', title: 'Analytics Dashboard', desc: 'Real-time revenue tracking, registration counts, check-in rates, and payment method breakdown.' },
              { icon: '🛍️', title: 'Merch Store', desc: 'Sell event merchandise alongside registrations. Size variants, stock management, and order fulfillment.' },
              { icon: '🎭', title: 'Theater Mode', desc: 'Live event display screen with sponsor logos, live attendance counter, and event branding.' },
              { icon: '📢', title: 'Carousel Display', desc: 'Featured events carousel on the homepage. Organisers can control which events and the display order.' },
              { icon: '👥', title: 'Multi-Organiser', desc: 'Add multiple co-organisers with logos and bios. All displayed in the Organised By section.' },
              { icon: '🔒', title: 'Gate Scanner', desc: 'QR code gate scanner with PIN protection. One-time scan enforcement. Real-time check-in tracking.' },
              { icon: '📋', title: 'Custom Forms', desc: 'Add unlimited custom fields to registration forms. Text, number, file upload, and dropdown types.' },
            ].map(f => (
              <div key={f.title} className="flex items-start gap-4 bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 hover:border-sky-500/20 transition-all">
                <span className="text-2xl shrink-0">{f.icon}</span>
                <div>
                  <h4 className="text-sm font-black text-white mb-1">{f.title}</h4>
                  <p className="text-gray-400 text-xs leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default DocsPage;